/**
 * FN-FE-13 — Időnaplózás UI (time entries, élő timer, számlázás, megosztás)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { get, post, patch, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState } from '../../components/ui';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import { useToast } from '../../components/ui/Toast';

// ── Types ──

interface TimeEntry {
  id: string; project_id: string; task_id: string | null; user_id: string;
  entry_date: string; duration_minutes: number; description: string | null;
  hourly_rate: number | null; is_billable: boolean; billable: boolean;
  is_billed: boolean; invoiced: boolean;
  project_name: string; user_name: string; created_at: string;
}

interface Project { id: string; name: string; partner_id?: string; partner_name?: string; }
interface User { id: string; name: string; email: string; }

interface ActiveTimer {
  user_id: string; project_id: string; task_id: string | null;
  started_at: string; description: string | null; project_name: string;
}

interface TimerLocal {
  projectId: string; projectName: string;
  taskId: string; description: string;
  startedAt: number; // ms epoch — szerver started_at alapján
}

// ── Helpers ──

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatTimerTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function weekAgoStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthStartStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// ── Main Component ──

export default function TimeTrackingPage() {
  const { toast } = useToast();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [totalBillableAmount, setTotalBillableAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'custom'>('week');
  const [dateFrom, setDateFrom] = useState(weekAgoStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [filterProject, setFilterProject] = useState('');
  const [filterBillable, setFilterBillable] = useState('');

  // Data
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  const [showTimerStartModal, setShowTimerStartModal] = useState(false);
  const [showTimerStopModal, setShowTimerStopModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Timer — szerver-szinkron állapot
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [timerLocal, setTimerLocal] = useState<TimerLocal | null>(null);
  const [timerDisplay, setTimerDisplay] = useState('0:00:00');
  const timerRef = useRef<number | null>(null);

  // ── Timer tick ──
  useEffect(() => {
    if (timerLocal) {
      const tick = () => {
        const elapsed = Math.floor((Date.now() - timerLocal.startedAt) / 1000);
        setTimerDisplay(formatTimerTime(elapsed));
      };
      tick();
      timerRef.current = window.setInterval(tick, 1000);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    } else {
      setTimerDisplay('0:00:00');
    }
  }, [timerLocal]);

  // ── Data Loading ──

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('from_date', dateFrom);
      if (dateTo) params.set('to_date', dateTo);
      if (filterProject) params.set('project_id', filterProject);
      const q = params.toString() ? `?${params}` : '';
      const data = await get<{ entries: TimeEntry[]; totalMinutes: number; totalBillableAmount: number }>(`/time-entries${q}`);
      setEntries(data.entries || []);
      setTotalMinutes(data.totalMinutes || 0);
      setTotalBillableAmount(data.totalBillableAmount || 0);
    } catch { toast('Bejegyzések betöltése sikertelen.', 'error'); }
    setLoading(false);
  }, [dateFrom, dateTo, filterProject]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await get<any>('/projects');
      setProjects(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await get<any>('/users');
      setUsers(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
  }, []);

  // Aktív timer lekérés a szerverről
  const loadActiveTimer = useCallback(async () => {
    try {
      const data = await get<ActiveTimer | { active: false }>('/active-timer');
      if (data && 'project_id' in data) {
        setActiveTimer(data as ActiveTimer);
        setTimerLocal({
          projectId: (data as ActiveTimer).project_id,
          projectName: (data as ActiveTimer).project_name,
          taskId: (data as ActiveTimer).task_id || '',
          description: (data as ActiveTimer).description || '',
          startedAt: new Date((data as ActiveTimer).started_at).getTime(),
        });
      } else {
        setActiveTimer(null);
        setTimerLocal(null);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadProjects(); loadUsers(); loadActiveTimer(); }, []);

  // Period presets
  useEffect(() => {
    if (period === 'today') { setDateFrom(todayStr()); setDateTo(todayStr()); }
    else if (period === 'week') { setDateFrom(weekAgoStr()); setDateTo(todayStr()); }
    else if (period === 'month') { setDateFrom(monthStartStr()); setDateTo(todayStr()); }
  }, [period]);

  // ── Timer Controls ──

  const startTimer = async (projectId: string, projectName: string, taskId: string, description: string) => {
    try {
      await post('/active-timer/start', { projectId, taskId: taskId || undefined, description: description || undefined });
      await loadActiveTimer();
      setShowTimerStartModal(false);
      toast('Időmérő elindítva.', 'success');
    } catch (e: any) {
      toast(e?.message || 'Timer indítása sikertelen.', 'error');
    }
  };

  const stopTimerAction = async (saveEntry: boolean, durationMinutes?: number, description?: string, billable?: boolean) => {
    try {
      if (saveEntry && activeTimer) {
        // Leállítjuk a servert és kézzel hozzuk létre a bejegyzést a kerekített adatokkal
        await del('/active-timer');
        await post('/time-entries', {
          projectId: activeTimer.project_id,
          taskId: activeTimer.task_id || undefined,
          entryDate: todayStr(),
          durationMinutes: durationMinutes || Math.max(1, Math.round((Date.now() - new Date(activeTimer.started_at).getTime()) / 60000)),
          description: description || activeTimer.description,
          isBillable: billable !== undefined ? billable : true,
        });
        toast('Időbejegyzés mentve.', 'success');
        loadEntries();
      } else {
        await del('/active-timer');
        toast('Timer elvetve.', 'info');
      }
      setActiveTimer(null);
      setTimerLocal(null);
      setShowTimerStopModal(false);
    } catch (e: any) {
      toast(e?.message || 'Hiba a timer leállításakor.', 'error');
    }
  };

  const getTimerMinutes = (): number => {
    if (!activeTimer) return 0;
    return Math.max(0, Math.round((Date.now() - new Date(activeTimer.started_at).getTime()) / 60000));
  };

  // ── FKeyBar ──

  useFKeyBar([
    { key: 'F2', label: 'Új bejegyzés', action: () => { setEditEntry(null); setShowEntryModal(true); } },
    { key: 'F3', label: timerLocal ? 'Timer leállítás' : 'Timer indítás', action: () => timerLocal ? setShowTimerStopModal(true) : setShowTimerStartModal(true) },
    { key: 'F5', label: 'Frissít', action: loadEntries },
    { key: 'F9', label: 'Megosztás', action: () => setShowShareModal(true) },
    { key: 'F10', label: 'Számla generálás', action: () => setShowInvoiceModal(true) },
  ]);

  // Filtered entries
  const filtered = filterBillable
    ? entries.filter(e =>
        filterBillable === 'billable' ? (e.is_billable && !e.is_billed) :
        filterBillable === 'invoiced' ? e.is_billed :
        !e.is_billable
      )
    : entries;

  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  return (
    <div>
      <PageHeader
        title="Időnaplózás"
        description={`${filtered.length} bejegyzés · ${totalHours}ó ${totalMins}p`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!timerLocal ? (
              <Button variant="secondary" onClick={() => setShowTimerStartModal(true)}>⏱ Timer indítás</Button>
            ) : null}
            <Button onClick={() => { setEditEntry(null); setShowEntryModal(true); }}>+ Új bejegyzés</Button>
          </div>
        }
      />

      {/* Élő timer sáv */}
      {timerLocal && (
        <div style={{ marginBottom: '24px', background: '#1a2540', color: '#fff', borderRadius: '12px', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '22px', fontFamily: 'monospace', fontWeight: 700 }}>{timerDisplay}</span>
            <span style={{ fontSize: '13px', color: '#cbd5e1' }}>
              {timerLocal.projectName}{timerLocal.description ? ` · ${timerLocal.description}` : ''}
            </span>
          </div>
          <Button size="sm" variant="danger" onClick={() => setShowTimerStopModal(true)}>Megállítás</Button>
        </div>
      )}

      {/* KPI kártyák */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <Card><div style={{ textAlign: 'center', padding: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Összes idő</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a2540' }}>{totalHours}ó {totalMins}p</div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Bejegyzések</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a2540' }}>{filtered.length}</div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Számlázható</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#F47B20' }}>
            {totalBillableAmount ? new Intl.NumberFormat('hu-HU').format(totalBillableAmount) + ' Ft' : '0 Ft'}
          </div>
        </div></Card>
        <Card><div style={{ textAlign: 'center', padding: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Nem számlázott</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#d97706' }}>
            {filtered.filter(e => e.is_billable && !e.is_billed).length} db
          </div>
        </div></Card>
      </div>

      {/* Szűrők */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {(['today', 'week', 'month', 'custom'] as const).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 500, border: 'none', cursor: 'pointer', background: period === p ? '#1a2540' : '#fff', color: period === p ? '#fff' : '#475569' }}>
              {p === 'today' ? 'Ma' : p === 'week' ? 'Hét' : p === 'month' ? 'Hónap' : 'Egyedi'}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input" style={{ width: '140px' }} />
            <span style={{ color: '#94a3b8' }}>—</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input" style={{ width: '140px' }} />
          </div>
        )}
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} className="form-input">
          <option value="">Minden projekt</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterBillable} onChange={e => setFilterBillable(e.target.value)} className="form-input">
          <option value="">Mind</option>
          <option value="billable">Számlázható (nem számlázott)</option>
          <option value="invoiced">Már számlázott</option>
          <option value="nonbillable">Nem-számlázható</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <Button size="sm" variant="ghost" onClick={() => setShowShareModal(true)}>🔗 Megosztás</Button>
          <Button size="sm" variant="ghost" onClick={() => setShowInvoiceModal(true)}>🧾 Számla generálás</Button>
        </div>
      </div>

      {/* Bejegyzések tábla */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '64px', color: '#94a3b8' }}>Betöltés...</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nincs bejegyzés" description="Rögzítsd az első időbejegyzésedet!" action={<Button onClick={() => { setEditEntry(null); setShowEntryModal(true); }}>+ Új bejegyzés</Button>} />
      ) : (
        <Card>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full text-sm" style={{ minWidth: '600px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Dátum</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Felhasználó</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Projekt</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Leírás</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Idő</th>
                  <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Állapot</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 600, color: '#475569', fontSize: '12px' }}>Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f8fafc' }} className="hover:bg-gray-50">
                    <td style={{ padding: '10px 16px', color: '#64748b', fontSize: '12px' }}>{formatDate(e.entry_date)}</td>
                    <td style={{ padding: '10px 16px', color: '#64748b' }}>{e.user_name || '—'}</td>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1a2540' }}>{e.project_name || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#64748b', maxWidth: '200px' }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '—'}</span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatDuration(e.duration_minutes)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      {e.is_billed
                        ? <Badge variant="success">Számlázva</Badge>
                        : e.is_billable
                          ? <Badge variant="warning">Számlázható</Badge>
                          : <Badge variant="default">Nem számlázható</Badge>
                      }
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!e.is_billed && (
                        <Button size="sm" variant="ghost" onClick={() => { setEditEntry(e); setShowEntryModal(true); }}>Szerkesztés</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={async () => {
                        try {
                          await post('/time-entries', {
                            projectId: e.project_id, taskId: e.task_id || undefined,
                            entryDate: todayStr(), durationMinutes: e.duration_minutes,
                            description: e.description, isBillable: e.is_billable,
                          });
                          toast('Bejegyzés klónozva.', 'success');
                          loadEntries();
                        } catch { toast('Klónozás sikertelen.', 'error'); }
                      }}>Klón</Button>
                      {!e.is_billed && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                          try { await del(`/time-entries/${e.id}`); toast('Bejegyzés törölve.', 'success'); loadEntries(); }
                          catch { toast('Törlés sikertelen.', 'error'); }
                        }} style={{ color: '#ef4444' }}>Törlés</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Kézi bejegyzés modal */}
      <EntryModal
        isOpen={showEntryModal} onClose={() => setShowEntryModal(false)}
        entry={editEntry} projects={projects}
        onSave={async (data) => {
          try {
            if (editEntry) {
              await patch(`/time-entries/${editEntry.id}`, data);
              toast('Bejegyzés frissítve.', 'success');
            } else {
              await post('/time-entries', data);
              toast('Bejegyzés létrehozva.', 'success');
            }
            setShowEntryModal(false);
            loadEntries();
          } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
        }}
      />

      {/* Timer indítás modal */}
      <TimerStartModal
        isOpen={showTimerStartModal} onClose={() => setShowTimerStartModal(false)}
        projects={projects} onStart={startTimer}
      />

      {/* Timer leállítás modal */}
      {activeTimer && (
        <TimerStopModal
          isOpen={showTimerStopModal} onClose={() => setShowTimerStopModal(false)}
          activeTimer={activeTimer} timerMinutes={getTimerMinutes()}
          onDiscard={() => stopTimerAction(false)}
          onSave={(mins, desc, billable) => stopTimerAction(true, mins, desc, billable)}
        />
      )}

      {/* Megosztás modal */}
      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} projects={projects} />

      {/* Számla generálás modal */}
      <InvoiceModal
        isOpen={showInvoiceModal} onClose={() => setShowInvoiceModal(false)}
        projects={projects} entries={entries}
        onInvoiceCreated={() => { loadEntries(); setShowInvoiceModal(false); }}
      />
    </div>
  );
}

// ── Kézi bejegyzés modal ──

function EntryModal({ isOpen, onClose, entry, projects, onSave }: {
  isOpen: boolean; onClose: () => void; entry: TimeEntry | null; projects: Project[];
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const { toast } = useToast();
  const [projectId, setProjectId] = useState('');
  const [entryDate, setEntryDate] = useState(todayStr());
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [hourlyRate, setHourlyRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [durationError, setDurationError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setProjectId(entry?.project_id || '');
      setEntryDate(entry?.entry_date?.split('T')[0] || todayStr());
      const dur = entry?.duration_minutes || 0;
      setHours(dur ? String(Math.floor(dur / 60)) : '');
      setMinutes(dur ? String(dur % 60) : '');
      setDescription(entry?.description || '');
      setBillable(entry?.is_billable ?? true);
      setHourlyRate(entry?.hourly_rate ? String(entry.hourly_rate) : '');
      setDurationError('');
    }
  }, [isOpen, entry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const durationMinutes = (parseInt(hours || '0') * 60) + parseInt(minutes || '0');
    if (durationMinutes < 1) {
      setDurationError('Az időtartam legalább 1 perc kell legyen.');
      toast('Az időtartam legalább 1 perc kell legyen.', 'warning');
      return;
    }
    setDurationError('');
    setSaving(true);
    await onSave({
      projectId, entryDate, durationMinutes,
      description: description || null,
      isBillable: billable,
      ...(hourlyRate ? { hourlyRate: Number(hourlyRate) } : {}),
    });
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={entry ? 'Bejegyzés szerkesztése' : '+ Új bejegyzés'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Dátum *" type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} required />
        <div className="space-y-1">
          <label>Projekt *</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} required className="form-input">
            <option value="">Válassz projektet...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label>Időtartam *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="number" value={hours} onChange={e => { setHours(e.target.value); setDurationError(''); }} placeholder="0" min="0" className={`form-input${durationError ? ' border-red-500' : ''}`} style={{ width: '80px', textAlign: 'center', ...(durationError ? { borderColor: '#ef4444' } : {}) }} />
            <span style={{ fontSize: '13px', color: '#64748b' }}>óra</span>
            <input type="number" value={minutes} onChange={e => { setMinutes(e.target.value); setDurationError(''); }} placeholder="0" min="0" max="59" className={`form-input${durationError ? ' border-red-500' : ''}`} style={{ width: '80px', textAlign: 'center', ...(durationError ? { borderColor: '#ef4444' } : {}) }} />
            <span style={{ fontSize: '13px', color: '#64748b' }}>perc</span>
          </div>
          {durationError && <p style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>{durationError}</p>}
        </div>
        <div className="space-y-1">
          <label>Leírás</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="form-input" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
          Számlázható ügyfél felé
        </label>
        {billable && <Input label="Óradíj (Ft/óra)" type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="0" />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>{entry ? 'Mentés' : 'Létrehozás'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Timer indítás modal ──

function TimerStartModal({ isOpen, onClose, projects, onStart }: {
  isOpen: boolean; onClose: () => void; projects: Project[];
  onStart: (projectId: string, projectName: string, taskId: string, description: string) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen) { setProjectId(''); setDescription(''); }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    const proj = projects.find(p => p.id === projectId);
    onStart(projectId, proj?.name || '', '', description);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="⏱ Időmérő indítása">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label>Projekt *</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} required className="form-input">
            <option value="">Válassz projektet...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label>Leírás</label>
          <input value={description} onChange={e => setDescription(e.target.value)} className="form-input" placeholder="Mit csinálsz?" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit">Indítás</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Timer leállítás modal ──

function TimerStopModal({ isOpen, onClose, activeTimer, timerMinutes, onDiscard, onSave }: {
  isOpen: boolean; onClose: () => void; activeTimer: ActiveTimer; timerMinutes: number;
  onDiscard: () => void;
  onSave: (minutes: number, description: string, billable: boolean) => void;
}) {
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [roundedMinutes, setRoundedMinutes] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setDescription(activeTimer.description || '');
      // 15 perces kerekítés felfelé (minimum 15 perc)
      setRoundedMinutes(timerMinutes > 0 ? (Math.ceil(timerMinutes / 15) * 15 || 15) : 15);
    }
  }, [isOpen, activeTimer, timerMinutes]);

  const h = Math.floor(timerMinutes / 60);
  const m = timerMinutes % 60;

  // Választható kerekítési lehetőségek (minimum 15 perc)
  const roundOptions = timerMinutes === 0
    ? [15, 30, 60]
    : Array.from(new Set([
        timerMinutes,
        Math.ceil(timerMinutes / 15) * 15,
        Math.ceil(timerMinutes / 30) * 30,
        Math.ceil(timerMinutes / 60) * 60,
      ])).filter(v => v > 0 && v <= timerMinutes + 60).sort((a, b) => a - b);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Időmérő leállítása">
      <div className="space-y-4">
        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Mért idő</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a2540' }}>{h} óra {m} perc</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Kerekítés:</span>
            <select value={roundedMinutes} onChange={e => setRoundedMinutes(Number(e.target.value))} className="form-input" style={{ width: 'auto' }}>
              {roundOptions.map(v => (
                <option key={v} value={v}>{formatDuration(v)}{v === timerMinutes ? ' (pontos)' : ''}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ fontSize: '13px' }}><span style={{ color: '#64748b' }}>Projekt:</span> <strong>{activeTimer.project_name}</strong></div>
        <div className="space-y-1">
          <label>Leírás</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="form-input" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
          Számlázható ügyfél felé
        </label>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', paddingTop: '8px' }}>
          <Button variant="ghost" onClick={onDiscard} style={{ color: '#ef4444' }}>Elvetés</Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="ghost" onClick={onClose}>Mégse</Button>
            <Button onClick={() => onSave(roundedMinutes, description, billable)}>Mentés</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Megosztás modal ──

function ShareModal({ isOpen, onClose, projects }: { isOpen: boolean; onClose: () => void; projects: Project[] }) {
  const { toast } = useToast();
  const [projectId, setProjectId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [password, setPassword] = useState('');
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [includeHourlyRate, setIncludeHourlyRate] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setProjectId(''); setValidUntil(''); setPassword(''); setShareUrl(''); }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!projectId) { toast('Válassz projektet!', 'warning'); return; }
    setSaving(true);
    try {
      const data = await post<any>(`/projects/${projectId}/shares`, {
        validUntil: validUntil || null,
        password: password || null,
        includeDescriptions,
        includeHourlyRate,
      });
      const url = `${window.location.origin}/public/timesheet/${data.share_token}`;
      setShareUrl(url);
      toast('Megosztási link létrehozva.', 'success');
    } catch (e: any) {
      toast(e?.message || 'Megosztás sikertelen.', 'error');
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🔗 Munkanapló megosztása">
      <div className="space-y-4">
        {!shareUrl ? (
          <>
            <div className="space-y-1">
              <label>Projekt *</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className="form-input">
                <option value="">Válassz projektet...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <Input label="Érvényes eddig (opcionális)" type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
            <Input label="Jelszóvédelem (opcionális)" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Hagyja üresen ha nem kell" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input type="checkbox" checked={includeDescriptions} onChange={e => setIncludeDescriptions(e.target.checked)} />
                Leírások megjelenítése
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input type="checkbox" checked={includeHourlyRate} onChange={e => setIncludeHourlyRate(e.target.checked)} />
                Óradíjak megjelenítése
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
              <Button variant="ghost" onClick={onClose}>Mégse</Button>
              <Button onClick={handleCreate} loading={saving}>Link létrehozása</Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600, marginBottom: '8px' }}>✓ Megosztási link kész</div>
              <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', color: '#1a2540', marginBottom: '12px' }}>{shareUrl}</div>
              <Button size="sm" onClick={() => { navigator.clipboard.writeText(shareUrl); toast('Link másolva!', 'success'); }}>
                Másolás vágólapra
              </Button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
              <Button onClick={onClose}>Bezárás</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Számla generálás modal ──

function InvoiceModal({ isOpen, onClose, projects, entries, onInvoiceCreated }: {
  isOpen: boolean; onClose: () => void; projects: Project[]; entries: TimeEntry[];
  onInvoiceCreated: () => void;
}) {
  const { toast } = useToast();
  const [projectId, setProjectId] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'task' | 'project' | 'user'>('task');
  const [issueDate, setIssueDate] = useState(todayStr());
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 15);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [step, setStep] = useState<'select' | 'preview'>('select');

  useEffect(() => {
    if (isOpen) { setProjectId(''); setPreview(null); setStep('select'); setNotes(''); }
  }, [isOpen]);

  // A kiválasztott projekt-szűrt, számlázható, még nem számlázott bejegyzések
  const unbilled = entries.filter(e =>
    e.is_billable && !e.is_billed && (!projectId || e.project_id === projectId)
  );
  const totalMins = unbilled.reduce((s, e) => s + e.duration_minutes, 0);

  // A partner meghatározása a projekt alapján
  const selectedProject = projects.find(p => p.id === projectId);

  const handlePreview = async () => {
    if (unbilled.length === 0) { toast('Nincs számlázandó bejegyzés.', 'warning'); return; }
    if (!selectedProject?.partner_id) {
      toast('A kiválasztott projekthez nincs partner rendelve.', 'error'); return;
    }
    setSaving(true);
    try {
      const data = await post<any>('/time-entries/preview-invoice', {
        partnerId: selectedProject.partner_id,
        timeEntryIds: unbilled.map(e => e.id),
        issueDate, dueDate, groupBy, notes: notes || null,
      });
      setPreview(data);
      setStep('preview');
    } catch (e: any) {
      toast(e?.message || 'Előnézet betöltése sikertelen.', 'error');
    }
    setSaving(false);
  };

  const handleGenerate = async () => {
    if (!selectedProject?.partner_id) return;
    setSaving(true);
    try {
      const data = await post<any>('/invoices/from-time-entries', {
        partnerId: selectedProject.partner_id,
        timeEntryIds: unbilled.map(e => e.id),
        issueDate, dueDate, groupBy, notes: notes || null,
      });
      toast(`Számla létrehozva: ${data.invoiceNumber}`, 'success');
      onInvoiceCreated();
    } catch (e: any) {
      toast(e?.message || 'Számla generálás sikertelen.', 'error');
    }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🧾 Számla generálás időnaplózásból">
      <div className="space-y-4">
        {step === 'select' ? (
          <>
            <div className="space-y-1">
              <label>Projekt szűrő</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className="form-input">
                <option value="">Minden projekt</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
              <strong>{unbilled.length}</strong> db számlázandó bejegyzés · <strong>{formatDuration(totalMins)}</strong>
              {unbilled.length === 0 && <span style={{ color: '#94a3b8', marginLeft: '8px' }}>(nincs számlázható adat)</span>}
            </div>

            {unbilled.length > 0 && (
              <div style={{ maxHeight: '160px', overflowY: 'auto', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#64748b' }}>Dátum</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#64748b' }}>Projekt</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', color: '#64748b' }}>Idő</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unbilled.slice(0, 30).map(e => (
                      <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '5px 10px' }}>{formatDate(e.entry_date)}</td>
                        <td style={{ padding: '5px 10px' }}>{e.project_name}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(e.duration_minutes)}</td>
                      </tr>
                    ))}
                    {unbilled.length > 30 && <tr><td colSpan={3} style={{ padding: '5px 10px', color: '#94a3b8', textAlign: 'center' }}>...és {unbilled.length - 30} további</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Input label="Kiállítás dátuma" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
              <Input label="Fizetési határidő" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>

            <div className="space-y-1">
              <label>Csoportosítás a számlán</label>
              <select value={groupBy} onChange={e => setGroupBy(e.target.value as any)} className="form-input">
                <option value="task">Feladatonként</option>
                <option value="project">Projektenként</option>
                <option value="user">Felhasználónként</option>
                <option value="none">Minden sor külön</option>
              </select>
            </div>

            <div className="space-y-1">
              <label>Megjegyzés (opcionális)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" placeholder="Megjelenik a számlán..." />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
              <Button variant="ghost" onClick={onClose}>Mégse</Button>
              <Button onClick={handlePreview} loading={saving} disabled={unbilled.length === 0}>Előnézet →</Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a2540', marginBottom: '4px' }}>Számla előnézet</div>
            {preview?.lines?.map((l: any, i: number) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                <span style={{ flex: 1, color: '#1a2540' }}>{l.description}</span>
                <span style={{ fontWeight: 600, marginLeft: '16px', whiteSpace: 'nowrap' }}>
                  {new Intl.NumberFormat('hu-HU').format(l.lineGross)} Ft
                </span>
              </div>
            ))}
            {preview?.totals && (
              <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Nettó:</span><strong>{new Intl.NumberFormat('hu-HU').format(preview.totals.net)} Ft</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>ÁFA (27%):</span><strong>{new Intl.NumberFormat('hu-HU').format(preview.totals.vat)} Ft</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e2e8f0' }}>
                  <span>Bruttó:</span><span style={{ color: '#F47B20' }}>{new Intl.NumberFormat('hu-HU').format(preview.totals.gross)} Ft</span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', paddingTop: '8px' }}>
              <Button variant="ghost" onClick={() => setStep('select')}>← Vissza</Button>
              <Button onClick={handleGenerate} loading={saving}>Számla létrehozása</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
